import { useRef, useState } from "react";
import { toast } from "react-toastify";
import SolicitacaoService from "../../services/resource/solicitacaoService";
import { processoInfo, etapaLabel } from "../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa } from "../../utils/solicitacoes/statusConfig";

const solicitacaoService = new SolicitacaoService();

/**
 * Orquestra o painel de "Encaminhar Solicitação": abre o painel, busca a prévia de atribuição da
 * próxima etapa e devolve uma Promise que só resolve quando o usuário confirma (com o
 * usuarioEscolhidoId, se aplicável) ou rejeita se ele cancelar. O painel fecha assim que confirma
 * — o loading da submissão real (executarAcao / criarSolicitacao / preencherX) continua no botão
 * de ação original de cada etapa, que já tem esse estado (acaoEmExecucao / enviando local).
 */
export function useEncaminhamento() {
    const [aberto, setAberto] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [preview, setPreview] = useState(null);
    const [contexto, setContexto] = useState(null);
    const promiseRef = useRef(null);

    function fechar() {
        setAberto(false);
        setCarregando(false);
        setPreview(null);
        setContexto(null);
        promiseRef.current = null;
    }

    function abrir(contextoInfo, buscarPreviewFn) {
        setContexto(contextoInfo);
        setPreview(null);
        setAberto(true);
        setCarregando(true);

        buscarPreviewFn()
            .then(res => setPreview(res.data))
            .catch(error => {
                console.error("Erro ao carregar prévia de encaminhamento", error);
                toast.error("Erro ao carregar informações de encaminhamento");
                promiseRef.current?.reject(error);
                fechar();
            })
            .finally(() => setCarregando(false));

        return new Promise((resolve, reject) => {
            promiseRef.current = { resolve, reject };
        });
    }

    function abrirParaAcao(solicitacao, acao) {
        return abrir(
            {
                tipoProcesso: solicitacao.tipoProcesso,
                processoLabel: processoInfo(solicitacao.tipoProcesso)?.label || solicitacao.tipoProcesso,
                numero: solicitacao.numero,
                etapaAtualLabel: solicitacao.etapaAtual
                    ? (etapaLabel(solicitacao.tipoProcesso, solicitacao.etapaAtual) || humanizarEtapa(solicitacao.etapaAtual))
                    : null,
            },
            () => solicitacaoService.previewEncaminhamentoAcao(solicitacao.id, acao)
        );
    }

    function abrirParaCriacao(tipoProcesso, etapaAtualCodigo, etapaDestinoCodigo, contextoCondicao = {}) {
        return abrir(
            {
                tipoProcesso,
                processoLabel: processoInfo(tipoProcesso)?.label || tipoProcesso,
                numero: null,
                etapaAtualLabel: etapaLabel(tipoProcesso, etapaAtualCodigo) || humanizarEtapa(etapaAtualCodigo),
            },
            () => solicitacaoService.previewEncaminhamentoCriacao(tipoProcesso, etapaDestinoCodigo, contextoCondicao)
        );
    }

    function confirmar(usuarioEscolhidoId) {
        promiseRef.current?.resolve(usuarioEscolhidoId);
        fechar();
    }

    function cancelar() {
        promiseRef.current?.reject(new Error("Encaminhamento cancelado"));
        fechar();
    }

    return {
        aberto, carregando, preview, contexto,
        abrirParaAcao, abrirParaCriacao, confirmar, cancelar,
    };
}
